import React, { useMemo, useRef, useState } from "react";

import { Box } from "@chakra-ui/react";
import { TYieldDisplayByType } from "api/business-user-management/types";
import {
  formatDateToDDMMMYYYY,
  formatDateToMMYYYY,
  handleAmountFormat,
  handleTruncateAmount,
} from "utils";
import { ONE_NAIRA_IN_KOBO } from "utils/constants";

import Pagination from "ui/pagination";
import { Status } from "ui/status";
import Table from "ui/table";
import TableWrapper from "ui/table/table-wrapper";
import { TSelectedTableRowIds } from "ui/table/types";

import { BusinessYieldTableHeaderCTAs } from "./BusinessYieldTableHeaderCTAs";
import {
  DEFAULT_YIELD_PAGINATION_PARAMS,
  daysFilterColumns,
  monthFilterColumns,
} from "./const";
import { IBusinessYieldTableProps } from "./types";

export const BusinessYieldTable = (props: IBusinessYieldTableProps) => {
  const {
    handleGetBusinessYield,
    isLoadingYield,
    businessYields,
    paginationData,
    displayByType,
  } = props;
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [selectedBusinessYieldIds, setSelectedBusinessYieldIds] =
    useState<TSelectedTableRowIds>({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const filterType: TYieldDisplayByType = useMemo(() => {
    if (!displayByType) {
      return "days";
    }
    return displayByType as TYieldDisplayByType;
  }, [displayByType]);

  const formattedData = businessYields.map((businessYield) => {
    const { createdAt, interest, principalAmount } = businessYield;

    let dateToShow = formatDateToDDMMMYYYY(createdAt);
    if (filterType === "month") {
      dateToShow = formatDateToMMYYYY(createdAt);
    }

    return {
      Date: <p className="text-sm text-[#667085]">{dateToShow}</p>,
      [displayByType === "month" ? "Average Balance" : "Balance"]: (
        <p className="text-sm font-semibold text-[#1A202C]">
          {handleAmountFormat({
            value: String(
              handleTruncateAmount(principalAmount) / ONE_NAIRA_IN_KOBO
            ),
            includeNairaSymbol: true,
            shouldFormatZero: true,
          })}
        </p>
      ),
      "Yield Earned": (
        <p className="text-sm text-[#667085]">
          {handleAmountFormat({
            value: String(handleTruncateAmount(interest) / ONE_NAIRA_IN_KOBO),
            includeNairaSymbol: true,
            shouldFormatZero: true,
          })}
        </p>
      ),
      ...(filterType === "month" && {
        "Yield Credited": (
          <Box>
            <Status
              status={businessYield?.yieldCredited ? "success" : "error"}
              text={businessYield?.yieldCredited ? "Yes" : "No"}
              className="font-semibold w-fit"
            />
          </Box>
        ),
      }),
      id: createdAt,
    };
  });

  return (
    <TableWrapper className="!p-0">
      <BusinessYieldTableHeaderCTAs
        handleGetBusinessYield={handleGetBusinessYield}
        isLoadingYield={isLoadingYield}
        isRefreshing={isRefreshing}
        setIsRefreshing={setIsRefreshing}
        filterType={filterType}
      />
      <Table
        columns={filterType === "days" ? daysFilterColumns : monthFilterColumns}
        data={formattedData}
        isLoading={isLoadingYield && !isRefreshing}
        hasCheckbox
        selectedTableRowIds={selectedBusinessYieldIds}
        setSelectedTableRowIds={setSelectedBusinessYieldIds}
        tableRef={tableRef}
      />
      <Pagination
        paginationData={paginationData}
        data={businessYields}
        defaultPaginationParams={DEFAULT_YIELD_PAGINATION_PARAMS}
        tableRef={tableRef}
        isLoadingTable={isLoadingYield}
      />
    </TableWrapper>
  );
};
